import Table from '../../../../../shared/Tables/Table'
import useTableWithRequest from '../../../../../shared/Tables/Table/ctrl/useTableWithRequest.ts'
import equipmentEventsTableFields from './equipmentEventsTable.fields.tsx'
import { IEquipment, IEquipmentEvent, ILiftErrorCode } from '../../../equipment.types.ts'
import React, { useCallback, useEffect, useState } from 'react'
import equipmentApi from '../../../equipment.api.ts'
import { IOption } from '@/types/global.ts'
import usePersistentState from '@/shared/hooks/usePersistentState'
import BadgeCount from '@/shared/Badges/BadgeCount'
import useHttpLoader from '@/shared/hooks/useHttpLoader.ts'
import AntDateRangeChipsPicker from '@/shared/Inputs/FilterChips/AntDateRangeChipsPicker/index.tsx'
import DropdownMultiFilterChips from '@/shared/Inputs/FilterChips/DropdownMultiFilterChips/index.tsx'
import DropdownSearchFilterChips from '@/shared/Inputs/FilterChips/DropdownSearchFilterChips/index.tsx'
import useSidePageWithTableCtrl from '@/shared/SidePage/useSidePageWithTableCtrl.ts'
import EquipmentEventSidePage from '@/pages/Equipment/components/SidePages/EquipmentEventSidePage'

interface IProps {
  equipmentId: IEquipment['id']
}

const eventTypeOptions: IOption<'WARNING' | 'ERROR' | 'FATAL_FAULTS' | 'CONNECT_AND_DISCONNECT' | 'IN_MAINTENANCE'>[] =
  [
    { title: 'Предупреждение', value: 'WARNING' },
    { title: 'Ошибка', value: 'ERROR' },
    { title: 'Критическая ошибка', value: 'FATAL_FAULTS' },
    {
      title: 'Режим обслуживания',
      value: 'IN_MAINTENANCE',
    },
    { title: 'Подкл. /  Откл.', value: 'CONNECT_AND_DISCONNECT' },
  ]

const sidePageIgnoredEventTypes: IEquipmentEvent['type'][] = [
  'CONNECT',
  'DISCONNECT',
  'IN_MAINTENANCE',
  'FROM_MAINTENANCE_TO_NORMAL',
]

const EquipmentEventsTable = (props: IProps) => {
  const [items, setItems] = useState<IEquipmentEvent[]>([])
  const [statusFilters, setStatusFilters] = useState(eventTypeOptions.map((o) => o.value))
  const [eventFilters, setEventFilters] = useState<ILiftErrorCode['code'][]>([])
  const [eventOptions, setEventOptions] = useState<IOption<ILiftErrorCode['description']>[]>([])
  const { wait, loading } = useHttpLoader()
  const [startDate, setStartDate] = usePersistentState<Date>(
    null,
    `/equipment/${props.equipmentId}/events/start_date`,
    'inMemory'
  )
  const [endDate, setEndDate] = usePersistentState<Date>(
    null,
    `/equipment/${props.equipmentId}/events/end_date`,
    'inMemory'
  )

  useEffect(() => {
    wait(equipmentApi.getLiftErrorCodes(props.equipmentId), (resp) => {
      if (resp.status === 'success') {
        setEventOptions(
          resp.body.map((item) => ({ title: `${item.description} (${item.numericCode})`, value: item.code }))
        )
      }
    })
  }, [])

  const handleDate = (startTs: number, endTs: number) => {
    setStartDate(startTs ? new Date(startTs) : null)
    setEndDate(endTs ? new Date(endTs) : null)
  }

  const dataLoad = useCallback(
    async (params: any) => {
      const parsedStatusFilters = statusFilters.reduce((acc, item) => {
        if (item === 'CONNECT_AND_DISCONNECT') acc.push('CONNECT', 'DISCONNECT')
        else acc.push(item)
        return acc
      }, [])

      return equipmentApi.getEquipmentEvents(props.equipmentId, {
        ...params,
        limit: 50,
        lpError: eventFilters,
        type: parsedStatusFilters,
        created_gte: startDate ? startDate.toISOString() : undefined,
        created_lte: endDate ? endDate.toISOString() : undefined,
      })
    },
    [props.equipmentId, statusFilters, startDate, endDate, eventFilters]
  )

  const tableCtrl = useTableWithRequest({
    serverSideOrder: false,
    fields: equipmentEventsTableFields,
    tableKey: `${props.equipmentId}/events`,
    itemKey: 'id',
    dataFetcher: dataLoad,
    items: items,
    setItems: setItems,
    storageType: 'regularState',
  })
  const sidePageCtrl = useSidePageWithTableCtrl<IEquipmentEvent>(items)

  const handleRowClick = useCallback(
    (item: IEquipmentEvent) => {
      if (sidePageIgnoredEventTypes.includes(item.type)) return
      sidePageCtrl.handleItemPageOpen(item)
    },
    [sidePageCtrl]
  )

  return (
    <>
      <div className="flex-space-between">
        <div className="inline-flex-gap gap8 center">
          <h4 className="text-heading-4">История событий</h4>
          <BadgeCount type="default" count={tableCtrl.totalItemsCount} loading={tableCtrl.loading} />
        </div>
        <div className="inline-flex-gap" style={{ flexWrap: 'wrap', justifyContent: 'end' }}>
          <div style={{ flexShrink: 0 }}>
            <AntDateRangeChipsPicker
              startTs={startDate?.getTime()}
              endTs={endDate?.getTime()}
              onChange={handleDate}
              allowSelectInFuture={false}
              withTime={false}
              style={{ minWidth: '179px' }}
              title="Дата события"
            />
          </div>
          <div style={{ flexShrink: 0 }}>
            <DropdownSearchFilterChips
              name="event"
              selectedOptions={eventFilters}
              title="Событие"
              options={eventOptions}
              style={{ minWidth: '140px' }}
              panelStyle={{ width: '360px' }}
              onSelect={setEventFilters}
              loading={loading}
              searchPlaceholder="Поиск по значениям"
            />
          </div>
          <div style={{ flexShrink: 0 }}>
            <DropdownMultiFilterChips
              name="event_type"
              selectedOptions={statusFilters}
              title="Тип события"
              options={eventTypeOptions}
              style={{ minWidth: '166px' }}
              panelStyle={{ width: '360px' }}
              onSelect={setStatusFilters}
            />
          </div>
        </div>
      </div>

      <Table {...tableCtrl} items={items as any} itemKey="id" onClick={handleRowClick} />
      <EquipmentEventSidePage
        open={sidePageCtrl.sidePageOpen}
        onClose={sidePageCtrl.handleClosePage}
        onCloseAnimationEnd={sidePageCtrl.handleCloseAnimationEnd}
        equipmentId={props.equipmentId}
        event={sidePageCtrl.selectedItem}
      />
    </>
  )
}

export default EquipmentEventsTable
